import React from 'react';
import * as Sentry from '@sentry/react';
import { isChunkLoadError } from '../lib/lazyWithRetry';

interface ErrorFallbackProps {
  error: Error;
  resetError: () => void;
}

function ErrorFallback({ error, resetError }: ErrorFallbackProps) {
  const chunkLoadError = isChunkLoadError(error);
  const handlePrimaryAction = () => {
    if (chunkLoadError) {
      window.location.reload();
      return;
    }

    resetError();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full text-center">
        <div className="mb-6">
          <svg
            className="mx-auto h-16 w-16 text-red-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          {chunkLoadError ? 'Update required' : 'Something went wrong'}
        </h1>
        <p className="text-gray-600 mb-6">
          {chunkLoadError
            ? 'Refresh the app to load the latest version.'
            : "We've been notified and are working to fix the issue."}
        </p>
        {import.meta.env.DEV && (
          <details className="mb-6 text-left bg-gray-100 rounded-lg p-4">
            <summary className="cursor-pointer font-medium text-gray-700">Error Details</summary>
            <pre className="mt-2 text-sm text-red-600 overflow-auto whitespace-pre-wrap">
              {error.message}
              {error.stack && `\n\n${error.stack}`}
            </pre>
          </details>
        )}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={handlePrimaryAction}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {chunkLoadError ? 'Refresh' : 'Try Again'}
          </button>
          <button
            onClick={() => (window.location.href = '/')}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    </div>
  );
}

interface AppErrorBoundaryProps {
  children: React.ReactNode;
}

export function AppErrorBoundary({ children }: AppErrorBoundaryProps) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => <ErrorFallback error={error} resetError={resetError} />}
      onError={(error, componentStack) => {
        // Additional error logging if needed
        console.error('Error caught by boundary:', error, componentStack);
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}

export default AppErrorBoundary;
